import { useMemo } from 'react'

import { Box, Text } from '@factorialco/deprecated-design-system'
import { F0Dialog } from '@factorialco/f0-react'
import { CandidateDuplicateInfoFragment } from 'generated/resources/graphql'

import i18n from 'modules/core/lib/i18n'

import { CandidateCard, CandidateCardCandidate } from './CandidateCard'

const formatMatchedFields = (
  matchersScores: CandidateDuplicateInfoFragment['matchersScores']
): string => {
  const fieldNames: Record<string, string> = {
    name_matcher: i18n.t('ats.candidate_details.duplicated.details.body.fields.name'),
    email_matcher: i18n.t('ats.candidate_details.duplicated.details.body.fields.email'),
    phone_matcher: i18n.t('ats.candidate_details.duplicated.details.body.fields.phone'),
    url_matcher: i18n.t('ats.candidate_details.duplicated.details.body.fields.personal_url'),
  }

  const matchedFieldNames = matchersScores
    .map((score) => fieldNames[score.matcherName])
    .filter((name): name is string => Boolean(name))

  if (matchedFieldNames.length <= 1) {
    return matchedFieldNames.join('')
  }

  const lastField = matchedFieldNames.pop()
  return `${matchedFieldNames.join(', ')} & ${lastField}`
}

const getDuplicateCandidate = (
  duplicate: CandidateDuplicateInfoFragment,
  currentCandidateId: string
) => {
  if (duplicate.matchedCandidate.id === currentCandidateId) {
    return duplicate.sourceCandidate
  }
  return duplicate.matchedCandidate
}

interface Props {
  open: boolean
  onClose: () => void
  currentCandidate: CandidateCardCandidate
  jobPostingTitle?: string | null
  duplicates: CandidateDuplicateInfoFragment[]
}

export const DuplicateCandidatesSidepanel = ({
  open,
  onClose,
  currentCandidate,
  jobPostingTitle,
  duplicates,
}: Props) => {
  const sortedDuplicates = useMemo(
    () => [...duplicates].sort((a, b) => b.score - a.score),
    [duplicates]
  )

  return (
    <F0Dialog
      position="right"
      isOpen={open}
      onClose={onClose}
      title={i18n.t('ats.candidate_details.duplicated.title')}
    >
      <Box gap="s24">
        <Box gap="s12">
          <Text.Normal weight="medium" color="textSecondary">
            {i18n.t('ats.candidate_details.duplicated.details.title')}
          </Text.Normal>
          <CandidateCard candidate={currentCandidate} jobPostingTitle={jobPostingTitle} />
        </Box>

        <Box gap="s12">
          <Box gap="s4">
            <Text.Normal weight="medium" color="textSecondary">
              {i18n.t('ats.candidate_details.duplicated.details.body.title')}
            </Text.Normal>
            <Text.Normal color="grey700">
              {i18n.t('ats.candidate_details.duplicated.details.body.description')}
            </Text.Normal>
          </Box>

          {sortedDuplicates.map((duplicate) => {
            const duplicateCandidate = getDuplicateCandidate(duplicate, currentCandidate.id)
            return (
              <CandidateCard
                key={duplicate.id}
                candidate={duplicateCandidate}
                jobPostingTitle={duplicateCandidate.applications[0]?.jobPosting?.title}
                applicationId={duplicateCandidate.applications[0]?.id}
                jobPostingId={duplicateCandidate.applications[0]?.jobPosting?.id}
                matchedFieldsText={formatMatchedFields(duplicate.matchersScores)}
              />
            )
          })}
        </Box>
      </Box>
    </F0Dialog>
  )
}
